import { c as _c } from "react/compiler-runtime";
import { ErrorBoundary } from "@/components/error-boundary";
import { useSearchPageMapData } from "@/hooks/search";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { hasProjectsWithSUPs } from "@/utils/map-helpers";
import { LoaderCircleIcon } from "lucide-react";
import React, { useMemo } from "react";
import { ListSkeleton } from "../list/skeleton";
import { MatchingProjectsList } from "./matching-projects-list";
import { NonMatchingProjectsList } from "./non-matching-projects-list";
interface Params {
  hideType: string;
  milestones: {
    id: string;
    type: string;
    milestone: string;
  }[];
}
export const getMilestones = ({
  milestones,
  hideType
}: Params): {
  id: string;
  type: string;
}[] | undefined => {
  if (hideType === "milestones") return undefined;
  if (hideType === "rejected") milestones = milestones.filter(x => x.milestone !== "rejected");
  return milestones.map(m => ({
    id: m.id,
    type: m.type
  }));
};
interface ProjectListProps {
  className?: string;
  style?: React.CSSProperties;
}
const ListViewFallback = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="text-center p-6"><div className="text-gray-800 font-semibold mb-2">List View Error</div><p className="text-gray-600 text-sm">Unable to load project list. Try refreshing the page.</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className="w-full h-full flex items-center justify-center bg-gray-50 relative">{t1}{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const ProjectList = React.memo(function ProjectList(t0) {
  const $ = _c(46);
  let className;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      className,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = style;
  } else {
    className = $[1];
    props = $[2];
    style = $[3];
  }
  const projectData = useSearchPageMapData(_temp);
  const isMapDataFetching = useSearchPageMapData(_temp2);
  const isNearByProjectDataFetching = useSearchPageMapData(_temp3);
  const isInitialBoundsFetch = useSearchPageMapData(_temp4);
  const {
    filters: t1
  } = useUrlFilters();
  const {
    selectedLocationId,
    locations
  } = t1;
  const isMapNearByProjectsFetching = isMapDataFetching || isNearByProjectDataFetching;
  let t2;
  if ($[4] !== projectData) {
    t2 = projectData.filter(_temp5);
    $[4] = projectData;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const matchingProjectData = t2;
  let t3;
  if ($[6] !== projectData) {
    t3 = projectData.filter(_temp6);
    $[6] = projectData;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const nonMatchingProjectData = t3;
  let t4;
  if ($[8] !== matchingProjectData) {
    t4 = hasProjectsWithSUPs(matchingProjectData);
    $[8] = matchingProjectData;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const hasMatchingPropertiesWithSUPs = t4;
  let t5;
  if ($[10] !== nonMatchingProjectData) {
    t5 = hasProjectsWithSUPs(nonMatchingProjectData);
    $[10] = nonMatchingProjectData;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const hasNonMatchingPropertiesWithSUPs = t5;
  const [isMounted, setIsMounted] = React.useState(false);
  const isXL = useMediaQuery("(min-width: 1280px)");
  const columnsCount = isXL ? 2 : 1;
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsMounted(true);
    };
    t7 = [];
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  React.useEffect(t6, t7);
  let t8;
  bb0: {
    if (selectedLocationId === "custom") {
      t8 = null;
      break bb0;
    }
    if (selectedLocationId && locations?.length) {
      let t9;
      if ($[14] !== locations || $[15] !== selectedLocationId) {
        let t10;
        if ($[17] !== selectedLocationId) {
          t10 = location => location.id === selectedLocationId;
          $[17] = selectedLocationId;
          $[18] = t10;
        } else {
          t10 = $[18];
        }
        t9 = locations.find(t10);
        $[14] = locations;
        $[15] = selectedLocationId;
        $[16] = t9;
      } else {
        t9 = $[16];
      }
      const selectedLocation = t9;
      if (selectedLocation?.name && selectedLocation.type === "project") {
        t8 = selectedLocation.name;
        break bb0;
      }
    }
    if (selectedLocationId && locations?.length === 1) {
      const firstLocation = locations[0];
      if (firstLocation?.name && firstLocation.type === "project") {
        t8 = firstLocation.name;
        break bb0;
      }
    }
    if (selectedLocationId && projectData.length > 0) {
      let t9;
      if ($[19] !== projectData || $[20] !== selectedLocationId) {
        let t10;
        if ($[22] !== selectedLocationId) {
          t10 = project => project.projectId === selectedLocationId;
          $[22] = selectedLocationId;
          $[23] = t10;
        } else {
          t10 = $[23];
        }
        t9 = projectData.find(t10);
        $[19] = projectData;
        $[20] = selectedLocationId;
        $[21] = t9;
      } else {
        t9 = $[21];
      }
      const selectedProject = t9;
      if (selectedProject?.projectName) {
        t8 = selectedProject.projectName;
        break bb0;
      }
    }
    t8 = null;
  }
  const locationName = t8;
  const hasNonMatchingProjects = nonMatchingProjectData.length > 0;
  const hasAnyProjects = hasMatchingPropertiesWithSUPs || hasNonMatchingPropertiesWithSUPs;
  let t9;
  bb1: {
    if (!selectedLocationId) {
      t9 = -1;
      break bb1;
    }
    let t10;
    if ($[24] !== projectData || $[25] !== selectedLocationId) {
      let t11;
      if ($[27] !== selectedLocationId) {
        t11 = project_0 => project_0.projectId === selectedLocationId;
        $[27] = selectedLocationId;
        $[28] = t11;
      } else {
        t11 = $[28];
      }
      t10 = projectData.filter(t11).reduce(_temp7, 0);
      $[24] = projectData;
      $[25] = selectedLocationId;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    t9 = t10;
  }
  const selectedLocationPropertyCount = t9;
  const shouldShowBanner = locationName && selectedLocationPropertyCount === 0 && hasAnyProjects;
  const t10 = `w-full ${className}`;
  let t11;
  if ($[29] !== columnsCount || $[30] !== hasAnyProjects || $[31] !== hasNonMatchingProjects || $[32] !== isInitialBoundsFetch || $[33] !== isMapNearByProjectsFetching || $[34] !== isMounted || $[35] !== locationName || $[36] !== matchingProjectData || $[37] !== nonMatchingProjectData || $[38] !== selectedLocationPropertyCount || $[39] !== shouldShowBanner) {
    t11 = isMapNearByProjectsFetching ? <div className="px-2 pt-2"><ListSkeleton count={6} /></div> : !isMounted ? <div className="flex flex-col w-full relative h-full overflow-y-auto"><div className="px-2 pt-2"><ListSkeleton count={6} /></div></div> : <div className="flex flex-col w-full relative h-full" style={{
      height: "100%",
      overflowY: "auto",
      contain: "strict"
    }}>{hasAnyProjects ? <div className="w-full h-full overflow-y-auto">{shouldShowBanner && <div className="px-4 py-3 bg-blue-50 border-l-4 border-blue-400"><p className="text-sm text-gray-700"><span className="font-semibold text-gray-900">0 properties in {locationName}</span><span className="block mt-1">Showing properties in and around {locationName}</span></p></div>}<MatchingProjectsList projectData={matchingProjectData} locationName={shouldShowBanner ? null : locationName} columnsCount={columnsCount} locationPropertyCount={selectedLocationPropertyCount} />{hasNonMatchingProjects && <NonMatchingProjectsList projectData={nonMatchingProjectData} columnsCount={columnsCount} />}</div> : <div className="flex flex-col items-center justify-center h-full px-4"><div className="text-center space-y-4 max-w-md mx-auto"><div className="text-6xl mb-4">🏠</div><h3 className="text-xl font-semibold text-gray-900">No properties found</h3><p className="text-gray-600">No properties match your current search criteria. Try adjusting your filters to see more results.</p><div className="mt-6 p-4 bg-gray-50 rounded-lg"><p className="text-sm font-medium text-gray-700 mb-3">Suggestions:</p><ul className="text-sm text-gray-600 space-y-2 text-left"><li className="flex items-start"><span className="text-gray-400 mr-2">•</span><span>Expand your location search radius</span></li><li className="flex items-start"><span className="text-gray-400 mr-2">•</span><span>Adjust your budget range</span></li><li className="flex items-start"><span className="text-gray-400 mr-2">•</span><span>Remove some active filters</span></li><li className="flex items-start"><span className="text-gray-400 mr-2">•</span><span>Try different property types</span></li></ul></div></div></div>}{isMapNearByProjectsFetching && isInitialBoundsFetch && hasAnyProjects && <div className="px-4 py-3 bg-white border-t border-gray-200"><div className="flex items-center justify-center"><LoaderCircleIcon className="animate-spin w-4 h-4 text-blue-600" /><span className="ml-2 text-sm text-gray-600">Loading more properties...</span></div></div>}{isMapNearByProjectsFetching && !isInitialBoundsFetch && hasAnyProjects && <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-20 flex items-start justify-center pt-4"><div className="flex items-center bg-white px-4 py-2 rounded-lg shadow-md"><LoaderCircleIcon className="animate-spin w-5 h-5 text-blue-600" /><span className="ml-2 text-sm font-medium text-gray-700">Loading properties...</span></div></div>}</div>;
    $[29] = columnsCount;
    $[30] = hasAnyProjects;
    $[31] = hasNonMatchingProjects;
    $[32] = isInitialBoundsFetch;
    $[33] = isMapNearByProjectsFetching;
    $[34] = isMounted;
    $[35] = locationName;
    $[36] = matchingProjectData;
    $[37] = nonMatchingProjectData;
    $[38] = selectedLocationPropertyCount;
    $[39] = shouldShowBanner;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== props || $[42] !== style || $[43] !== t10 || $[44] !== t11) {
    t12 = <ErrorBoundary fallback={ListViewFallback}><div className={t10} style={style} {...props}>{t11}</div></ErrorBoundary>;
    $[41] = props;
    $[42] = style;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  return t12;
});
function _temp(state) {
  return state.nearByprojects;
}
function _temp2(state_0) {
  return state_0.isMapDataFetching;
}
function _temp3(state_1) {
  return state_1.isNearByProjectDataFetching;
}
function _temp4(state_2) {
  return state_2.isInitialBoundsFetch;
}
function _temp5(t0) {
  const {
    isExactMatch
  } = t0;
  return Boolean(isExactMatch);
}
function _temp6(t0) {
  const {
    isExactMatch: isExactMatch_0
  } = t0;
  return !Boolean(isExactMatch_0);
}
function _temp7(total, project_1) {
  return total + (project_1.sups?.length || 0);
}